<template>
    <div>
        <kgr-access-medium-scanner
            :club-id="clubId"
            access-medium-scanned-as-event
            @accessMediumScanned="onAccessMediumScanned($event)"
        />

        <kgr-form-scaffold
            :with-primary-buttons="false"
            @backBtnClicked="redirectToOverview"
        >
            <div class="kgr-row">
                <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                    <div class="kgr-grid__cell">
                        <div class="kgr-inputGroup">
                            <label class="kgr-inputGroup__label">{{ $t('Stichtag') }}</label>
                            <kgr-date-picker
                                :value="selectedDate.format('DD.MM.YYYY')"
                                @change="onDateSelected($event)"
                            />
                        </div>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-text-input
                            v-model="search"
                            :label="$t('Mitgliedersuche')"
                            @post-input="inputSearch"
                        />
                    </div>
                </div>

                <div class="kgr-grid__cell top-space">
                    <checkbox
                        :checked="memberWithAttendancesOnly"
                        :label="$t('Nur Mitglieder mit Anwesenheiten anzeigen')"
                        @change="onMemberWithAttendancesOnly()"
                    />
                </div>

                <div class="kgr-grid__cell top-space">
                    <checkbox
                        :checked="memberWithOpenAttendancesOnly"
                        :label="$t('Nur aktuell anwesende Mitglieder anzeigen')"
                        @change="onMemberWithOpenAttendancesOnly()"
                    />
                </div>
            </div>

            <div class="kgr-row">
                <loading
                    v-if="pendingLoadMemberAttendances"
                />

                <kgr-member-attendances
                    v-else
                    :attendances-by-members="memberAttendances"
                    :is-current-day="isCurrentDay"
                    :pending-actions="pendingActions"
                    :attendance-in-edit-mode="attendanceInEditMode"
                    :pending-attendances="pendingAttendances"
                    class="animated fadeIn"
                    @cancelEdit="attendanceInEditMode = null"
                    @actionTriggered="triggerAttendanceAction($event)"
                    @editAttendance="attendanceInEditMode = $event"
                    @attendanceUpdated="onAttendanceUpdated($event)"
                    @attendanceRemoved="onAttendanceRemoved($event)"
                />
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import AccessMediumScanner from '../../components/club/access-medium/AccessMediumScanner.vue';
import Checkbox from '../../components/common/form/checkbox/Checkbox.vue';
import DatePicker from '../../components/common/form/picker/DatePicker.vue';
import DepartmentRepository from '../../js/repository/DepartmentRepository.js';
import KgrFormScaffold from '../../components/common/form/FormScaffold.vue';
import KgrMemberAttendances from '../../components/members/member-attendances/MemberAttendances.vue';
import KgrTextInput from '../../components/common/form/input/TextInput.vue';
import Loading from '../../components/common/Loading.vue';
import MemberRepository from '../../js/repository/MemberRepository.js';
import moment from 'moment';

export default {
    components: {
        KgrTextInput,
        Checkbox,
        Loading,
        'kgr-access-medium-scanner': AccessMediumScanner,
        KgrMemberAttendances,
        'kgr-date-picker': DatePicker,
        'kgr-form-scaffold': KgrFormScaffold,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        departmentId: {
            type: Number,
            required: true,
        },
        departmentName: {
            type: String,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        }
    },
    data() {
        return {
            selectedDate: moment(),
            pendingActions: [],
            pendingAttendances: [],
            pendingLoadMemberAttendances: true,
            memberAttendances: [],
            memberWithAttendancesOnly: false,
            memberWithOpenAttendancesOnly: false,
            attendanceInEditMode: null,
            search: '',
            searchTimer: null,
        }
    },
    computed: {
        isCurrentDay() {
            return moment().format('DD.MM.YYYY') === this.selectedDate.format('DD.MM.YYYY');
        },
    },
    mounted() {
        this.loadAttendances();
    },
    methods: {
        async loadAttendances() {
            this.memberAttendances = [];
            this.pendingLoadMemberAttendances = true;
            DepartmentRepository.findMemberAttendancesForDate(
                this.departmentId,
                this.selectedDate.format('YYYY-MM-DD'),
                this.search,
                this.memberWithAttendancesOnly,
                this.memberWithOpenAttendancesOnly
            ).then(memberAttendances => {
                this.memberAttendances = memberAttendances;
                this.pendingLoadMemberAttendances = false;
            }).catch(error => {
                this.pendingLoadMemberAttendances = false;
                this.$notify.error(error.message);
            });
        },
        onAttendanceUpdated(event) {
            this.pendingAttendances.push(event.attendanceId);
            DepartmentRepository.updateMemberAttendance(event.attendanceId, event.start, event.end)
                .then(() => {
                    if (this.attendanceInEditMode === event.attendanceId) {
                        this.attendanceInEditMode = null;
                    }
                    this.removePendingAttendance(event.attendanceId);
                    this.$notify.success('Anwesenheitszeit erfolgreich gespeichert');
                    this.loadAttendances();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        onAttendanceRemoved(event) {
            this.pendingAttendances.push(event.attendanceId);
            DepartmentRepository.removeMemberAttendance(event.attendanceId)
                .then(() => {
                    this.$notify.success('Anwesenheitszeit erfolgreich entfernt');
                    this.loadAttendances();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        removePendingAttendance(attendanceId) {
            const index = this.pendingAttendances.indexOf(attendanceId);
            if (index !== -1) {
                this.pendingAttendances.splice(index, 1);
            }
        },
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        onDateSelected(selection) {
            this.selectedDate = moment(selection, 'DD-MM-YYYY');
            this.pendingLoadMemberAttendances = true;
            this.loadAttendances();
        },
        onMemberWithAttendancesOnly() {
            this.memberWithAttendancesOnly = !this.memberWithAttendancesOnly
            if (!this.memberWithAttendancesOnly) {
                this.memberWithOpenAttendancesOnly = false;
            }
            this.loadAttendances();
        },
        onMemberWithOpenAttendancesOnly() {
            this.memberWithOpenAttendancesOnly = !this.memberWithOpenAttendancesOnly
            if (this.memberWithOpenAttendancesOnly) {
                this.memberWithAttendancesOnly = true;
            }
            this.loadAttendances();
        },
        inputSearch() {

            if ((this.search.trim().length) < 3) {
                return;
            }
            clearTimeout(this.searchTimer);

            this.searchTimer = setTimeout(function () {
                this.loadAttendances();
            }.bind(this), 600);
        },
        onAccessMediumScanned(memberId) {
            this.triggerAttendanceAction(memberId);
        },
        triggerAttendanceAction(memberId) {
            if (!this.isCurrentDay) {
                return;
            }

            if (this.pendingActions.includes(memberId)) {
                return;
            }

            this.pendingActions.push(memberId);

            MemberRepository
                .triggerDepartmentAttendanceAction(memberId, this.departmentId)
                .then(() => {
                    this.removeMemberIdFromPendingActions(memberId);
                    this.$notify.success('Anwesenheitszeit erfolgreich gespeichert');
                    this.loadAttendances();
                })
                .catch(error => {
                    this.removeMemberIdFromPendingActions(memberId);
                    if (error.status === 400) {
                        this.$notify.error('memberAttendances.memberNotActive', {name: this.departmentName})
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
        },
        removeMemberIdFromPendingActions(memberId) {
            this.pendingActions = this.pendingActions.filter(pendingId => pendingId !== memberId);
        },
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
    .date-center > * {
        text-align: center;
    }

    .top-space {
        padding-top: 27px;
    }
</style>
